import { useState, useEffect } from 'react';

const socials = [
  { icon: 'ri-instagram-line', href: 'https://www.instagram.com/debsartdesigns', label: 'Instagram' },
  { icon: 'ri-facebook-circle-line', href: 'https://www.facebook.com/debsartdesigns', label: 'Facebook' },
  { icon: 'ri-pinterest-line', href: 'https://www.pinterest.com/Storytellerdebsartdesigns', label: 'Pinterest' },
  { icon: 'ri-youtube-line', href: 'https://www.youtube.com/@debsartdesigns', label: 'YouTube' },
  { icon: 'ri-tiktok-line', href: 'https://www.tiktok.com/@debsartdesigns', label: 'TikTok' },
];

const navItems = [
  { label: 'Home', id: 'hero' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [stickyVisible, setStickyVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 80);
      setStickyVisible(window.scrollY > 500);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    if (id === 'hero') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
          scrolled
            ? 'bg-white/96 backdrop-blur-md border-b border-[#1A1612]/8 shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full flex items-center justify-between px-6 md:px-10 lg:px-16 py-3">
          {/* Logo — 2x larger */}
          <a href="/" className="flex items-center cursor-pointer shrink-0">
            <img
              src="https://images.squarespace-cdn.com/content/v1/6205743531e70503b4cb51e0/a95e9e87-a806-44f3-b172-8270da5024a9/logo+gold.png?format=1500w"
              alt="Deb's Art Designs"
              className="h-24 md:h-28 w-auto object-contain"
            />
          </a>

          {/* Desktop nav — centered */}
          <nav className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className={`whitespace-nowrap text-[10px] uppercase tracking-[0.35em] font-medium transition-colors duration-300 cursor-pointer ${scrolled ? 'text-[#1A1612]/50 hover:text-[#C9A96E]' : 'text-white/55 hover:text-white'}`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right: socials + CTA */}
          <div className="hidden md:flex items-center gap-5">
            <div className="flex items-center gap-3">
              {socials.slice(0, 3).map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                  className={`w-5 h-5 flex items-center justify-center transition-colors cursor-pointer ${scrolled ? 'text-[#1A1612]/30 hover:text-[#C9A96E]' : 'text-white/30 hover:text-[#C9A96E]'}`}>
                  <i className={`${s.icon} text-xs`}></i>
                </a>
              ))}
            </div>
            <div className={`w-px h-4 ${scrolled ? 'bg-[#1A1612]/10' : 'bg-white/10'}`}></div>
            <button
              onClick={() => scrollTo('contact')}
              className={`whitespace-nowrap px-6 py-2.5 border text-[10px] font-semibold uppercase tracking-[0.3em] transition-all duration-400 cursor-pointer ${scrolled ? 'border-[#C9A96E]/60 text-[#C9A96E] hover:bg-[#C9A96E] hover:text-white' : 'border-[#C9A96E]/40 text-[#C9A96E] hover:bg-[#C9A96E]/15 hover:border-[#C9A96E]'}`}
            >
              Commission a Piece
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer transition-colors ${scrolled ? 'text-[#1A1612]/60 hover:text-[#1A1612]' : 'text-white/60 hover:text-white'}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-[#1A1612]/8 px-6 py-8 flex flex-col gap-6">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="text-left text-[10px] uppercase tracking-[0.35em] font-medium text-[#1A1612]/50 hover:text-[#C9A96E] transition-colors cursor-pointer"
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => scrollTo('contact')}
              className="whitespace-nowrap mt-2 px-6 py-3.5 border border-[#C9A96E]/50 text-[#C9A96E] text-[10px] font-semibold uppercase tracking-[0.3em] hover:bg-[#C9A96E] hover:text-white transition-all duration-400 cursor-pointer text-center"
            >
              Commission a Piece
            </button>
            <div className="flex items-center gap-4 pt-2">
              {socials.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                  className="w-7 h-7 flex items-center justify-center text-[#1A1612]/30 hover:text-[#C9A96E] transition-colors cursor-pointer">
                  <i className={`${s.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Sticky floating CTA */}
      <div className={`fixed bottom-8 right-8 z-40 transition-all duration-500 ${stickyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
        <button
          onClick={() => scrollTo('contact')}
          className="whitespace-nowrap flex items-center gap-3 px-6 py-3.5 bg-[#C9A96E] text-white text-[10px] font-semibold uppercase tracking-[0.3em] hover:bg-[#b8956a] transition-all duration-300 cursor-pointer"
        >
          <i className="ri-brush-line text-xs"></i>
          Commission a Piece
        </button>
      </div>
    </>
  );
};

export default Navbar;
